import { useEffect, useId, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { totpCode } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { PoweredBy } from '../components/States.tsx';
import type { Tenant } from '../tenant/tenant.ts';
import { listFactors, startEnrolment, verifyCode, type Enrolment, type Factor } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The six-digit code box and its button; `onCode` throws the words to show. */
function CodeForm({
  label,
  submit,
  onCode,
}: {
  readonly label: string;
  readonly submit: string;
  readonly onCode: (code: string) => Promise<void>;
}): ReactElement {
  const id = useId();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(e: FormEvent): Promise<void> {
    e.preventDefault();
    const code = totpCode(value);
    if (!code) {
      setError('Enter the 6-digit code shown in your authenticator app.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onCode(code);
    } catch (err) {
      setError(message(err));
      setValue('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={(e) => void send(e)} noValidate>
      <label className="label muted" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input data"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={9}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <p id={`${id}-error`} className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" variant="primary" busy={busy}>
        {busy ? 'Checking…' : submit}
      </Button>
    </form>
  );
}

/** Enter the code from an app already set up (the second step of signing in, or before a sensitive change). */
export function TwoStepCode({
  sb,
  onDone,
}: {
  readonly sb: SupabaseClient;
  readonly onDone: () => void;
}): ReactElement {
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listFactors(sb)
      .then((f) => {
        setFactors(f);
        setChosen(f[0]?.id ?? null);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);

  if (error)
    return (
      <p className="field__error body" role="alert">
        {error}
      </p>
    );
  if (!factors) return <p className="caption muted">Reading your authenticator apps…</p>;
  if (factors.length === 0 || !chosen)
    return <p className="body">This account has no authenticator app set up yet.</p>;
  return (
    <div className="stack">
      {factors.length > 1 && (
        <fieldset className="stack">
          <legend className="label muted">Which app</legend>
          {factors.map((f) => (
            <label key={f.id} className="row body">
              <input
                type="radio"
                name="two-step-factor"
                checked={chosen === f.id}
                onChange={() => setChosen(f.id)}
              />
              {f.name}
            </label>
          ))}
        </fieldset>
      )}
      <CodeForm
        label="Code from your authenticator app"
        submit="Continue"
        onCode={async (code) => {
          await verifyCode(sb, chosen, code);
          onDone();
        }}
      />
    </div>
  );
}

/** Set up an authenticator app: name it, scan the QR code (or type the key), confirm with a code. */
export function TwoStepEnrol({
  sb,
  onDone,
  onCancel,
}: {
  readonly sb: SupabaseClient;
  readonly onDone: () => void;
  readonly onCancel?: () => void;
}): ReactElement {
  const nameId = useId();
  const [name, setName] = useState('My phone');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setEnrolment(await startEnrolment(sb, name));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (!enrolment) {
    return (
      <form className="stack" onSubmit={(e) => void start(e)} noValidate>
        <p className="body">
          You need an authenticator app on your phone, for example Microsoft Authenticator, Google
          Authenticator or 1Password. Each time you sign in, the app shows a 6-digit code that changes every
          30 seconds.
        </p>
        <label className="label muted" htmlFor={nameId}>
          Name of this phone or app
        </label>
        <input
          spellCheck
          id={nameId}
          className="input"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
        />
        {error && (
          <p className="field__error caption" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <Button type="submit" variant="primary" busy={busy}>
            Set up the app
          </Button>
          {onCancel && (
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    );
  }
  return (
    <div className="stack">
      <p className="body">
        In the authenticator app, add an account and scan this code. If you cannot scan it, type the key below
        instead.
      </p>
      {enrolment.qrCode && (
        <img className="two-step__qr" src={enrolment.qrCode} alt="QR code for your authenticator app" />
      )}
      <p className="caption muted">
        Key: <span className="data two-step__secret">{enrolment.secret}</span>
      </p>
      <CodeForm
        label="Code shown in the app"
        submit="Turn on two-step sign-in"
        onCode={async (code) => {
          await verifyCode(sb, enrolment.factorId, code);
          onDone();
        }}
      />
      {onCancel && (
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
}

/** A sign-in card for the second step (the code, or setting up the app when it is required). */
export function TwoStepCard({
  tenant,
  title,
  children,
  onSignOut,
}: {
  readonly tenant: Tenant;
  readonly title: string;
  readonly children: ReactNode;
  readonly onSignOut: () => void;
}): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="two-step-title">
        {tenant.logo ? (
          <img className="signin__logo" src={tenant.logo} alt={tenant.name} />
        ) : (
          <span className="heading">{tenant.name}</span>
        )}
        <h1 id="two-step-title" className="title">
          {title}
        </h1>
        {children}
        <Button variant="quiet" onClick={onSignOut}>
          Sign out
        </Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
      </footer>
    </div>
  );
}
